import { getId, getPath, normalizeUrlSlashes } from '@/mgnl/utils/magnolia.js';

export function decodeIfEscaped(text) {
    if (!text) return '';

    if (text.includes('&lt;') || text.includes('&gt;')) {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        return doc.documentElement.textContent;
    }
    return text;
}

/**
 * Makes a Magnolia link absolute by prefixing `environment.mgnlBaseUrl`.
 * Assumption (based on current Magnolia output): `inputUrl` already includes the context path, e.g. "/local/dam/...".
 *
 * If `inputUrl` is already absolute (http/https), it is returned normalized.
 *
 * @param {string} inputUrl
 * @param {MagnoliaEnvironment} [environment]
 * @returns {string} Absolute URL (or empty string if it cannot be resolved).
 */
export function toAbsoluteMagnoliaUrl(inputUrl, environment) {
    const raw = typeof inputUrl === 'string' ? inputUrl.trim() : '';
    if (!raw) return '';

    // Already absolute (http/https)
    if (/^https?:\/\//i.test(raw)) {
        return normalizeUrlSlashes(raw);
    }

    const baseUrl = environment?.mgnlBaseUrl?.trim();
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    return normalizeUrlSlashes(`${baseUrl}${path}`);
}

/**
 * @typedef {Object} MagnoliaEnvironment
 * @property {string} appBase - Base path of the consuming app (used to build routing/context).
 * @property {string} mgnlBaseUrl - Base URL of the Magnolia instance (e.g. author/public host).
 * @property {string} mgnlContextPath - Magnolia context path (often '/' or something like '/magnoliaPublic').
 * @property {string[]} languages - Supported language codes (e.g. ['de','fr','it']).
 */
/**
 * @typedef {Object} GetLinkAttributesOptions
 * @property {Object} link - The Magnolia link object (e.g. contains linkTitle, trackingLabel, and link details).
 * @property {string} [dataTracking] - Optional tracking identifier (overrides/extends link tracking data).
 * @property {MagnoliaEnvironment} [environment] - Optional environment info (only needed if URLs must be resolved/rewritten).
 */

/**
 * Rebuild of the Freemarker macro 'getLinkAttributes' from links.ftl.
 *
 * @param {GetLinkAttributesOptions} options
 * @returns {LinkProperties} Structured link attributes.
 */
export function getLinkAttributes({ link, dataTracking = '', environment }) {
    if (!link) {
        return {
            type: '',
            url: '',
            text: '',
            cssClasses: '',
            trackingLabel: '',
        };
    }

    const linkTitle = link.linkTitle || '';
    const trackingLabel = link.trackingLabel || '';
    const linkData = link.link || {};
    const linkType = linkData.linkType;

    if (linkType === 'internal' && linkData.internalLink) {
        const pageLink = linkData.internalLink?.['@link'] ?? linkData.internalLink ?? '';
        const linkPostfix = linkData.linkPostfix || '';

        return {
            type: 'internal',
            url: pageLink + linkPostfix,
            text: linkTitle,
            cssClasses: dataTracking ? '' : 'Internal--link',
            trackingLabel: trackingLabel,
            ...(linkData.internalLink?.['@path'] ? { internalLinkPath: linkData.internalLink?.['@path'] ?? '' } : {}),
        };
    }

    if (linkType === 'external') {
        return {
            type: 'external',
            url: linkData.externalLink || '',
            text: linkTitle,
            cssClasses: dataTracking ? '' : 'External--link',
            trackingLabel: trackingLabel,
        };
    }

    if (linkType === 'downloadPDF') {
        const downloadLink = linkData.downloadPDF?.['@link'] ?? '';
        const assetMimeType = linkData.downloadPDF?.mimeType ?? '';
        let assetExtension = '';

        if (assetMimeType.includes('/')) {
            assetExtension = assetMimeType.split('/')[1];
        }

        // needs an absolute URL here for headless consumption
        const absoluteDownloadUrl = toAbsoluteMagnoliaUrl(downloadLink, environment);

        return {
            type: 'download',
            url: absoluteDownloadUrl,
            text: linkTitle,
            cssClasses: dataTracking ? '' : 'Download--link',
            trackingLabel: trackingLabel,
            mimeType: assetMimeType,
            extension: assetExtension,
        };
    }

    if (linkType === 'productCatalog') {
        const productCatalogDocument = linkData.productCatalogDocument ?? {};
        const productCatalogFile = productCatalogDocument.file ?? {};
        const downloadLink = productCatalogFile?.['@link'] ?? '';
        const assetMimeType = productCatalogFile?.mimeType ?? '';
        let assetExtension = '';

        if (assetMimeType.includes('/')) {
            assetExtension = assetMimeType.split('/')[1];
        }

        return {
            type: 'download',
            url: downloadLink,
            text: linkTitle || productCatalogDocument.fileName || '',
            cssClasses: dataTracking ? '' : 'Download--link',
            trackingLabel: trackingLabel,
            mimeType: assetMimeType,
            extension: assetExtension,
        };
    }

    if (linkType === 'modal') {
        const modalLink = linkData.modal || '';
        return {
            type: 'modal',
            url: '#' + modalLink,
            text: linkTitle,
            cssClasses: dataTracking ? '' : 'Modal--link',
            trackingLabel: trackingLabel,
        };
    }

    return {
        type: '',
        url: '',
        text: '',
        cssClasses: '',
        trackingLabel: '',
    };
}

/**
 * Helper function to generate basic image attributes from a Magnolia asset and its theme-configured renditions.
 *
 * Produces a normalized `src` URL (fallback: first rendition, otherwise `image['@link']`) and,
 * if renditions are available, a responsive `srcset` string filtered by `maxWidth`.
 *
 * @param {MagnoliaEnvironment} environment - Magnolia environment configuration.
 * @param {Object} image - Magnolia DAM asset object (typically contains `@link`, `metadata`, `renditions`).
 * @param {number|null} [maxWidth=null] - Optional upper bound for generated `srcset` widths.
 * @returns {ImageProperties} Structured image attributes (`src`, optional `srcset`, `alt`).
 */
export function getImageAttributes(environment, image, maxWidth = null) {
    const { metadata, renditions: mgnlRenditions } = image ?? {};

    const renditions = Object.entries(mgnlRenditions ?? {});

    const srcset = renditions
        .filter(([width]) => !maxWidth || parseInt(width) <= maxWidth)
        .map(([width, data]) => `${toAbsoluteMagnoliaUrl(data.link, environment)}  ${width}w`)
        .join(', ');

    // first rendition fallback to original
    const src = toAbsoluteMagnoliaUrl(renditions?.[0]?.[1]?.link ?? image?.['@link'], environment);
    const alt = `${metadata?.description ?? image?.['@name']}`;

    return {
        src,
        srcset: srcset || src,
        alt,
    };
}

/**
 * Generate image attributes for Magnolia "focalarea" imaging endpoint.
 *
 * Builds `src` and (if available) `srcset` URLs using the focalarea imaging servlet:
 * `/.imaging/focalarea/{format}/{width}x/dam/{assetId}{assetPath}`
 *
 * Allowed `format` values are the keys of the Magnolia template formats map:
 * `square`, `3x2`, `2x3`, `portrait`, `landscape`, `banner`, `5x4`, `4x5`, `video-portrait`, `video`.
 *
 * @param {Object} environment - Magnolia environment configuration.
 * @param {string} environment.mgnlBaseUrl - Base URL, e.g. `http://localhost:8080`.
 * @param {string} environment.mgnlContextPath - Context path, e.g. `/local`.
 * @param {Object} image - Magnolia DAM asset object (must contain `@link` and typically renditions/metadata).
 * @param {'square'|'3x2'|'2x3'|'portrait'|'landscape'|'banner'|'5x4'|'4x5'|'video-portrait'|'video'} [format='square']
 *   - Focalarea format preset.
 * @param {number|null} [maxWidth=null] - Optional upper bound for generated `srcset` widths.
 * @returns {ImageProperties} Structured image attributes (`src`, optional `srcset`, `alt`).
 */

export function getFocalAreaImageAttributes(environment, image, format = 'square', maxWidth = null) {
    const { mgnlBaseUrl, mgnlContextPath } = environment;
    const { metadata, renditions: mgnlRenditions } = image ?? {};
    const imgLink = image?.['@link'] ?? '';
    const imgId = getId(image);
    const imgPath = getPath(image);

    const renditions = Object.entries(mgnlRenditions ?? {});

    const srcBase = `${mgnlBaseUrl}${mgnlContextPath}/.imaging/focalarea/${format}`;
    const srcset = renditions
        .filter(([width]) => !maxWidth || parseInt(width) <= maxWidth)
        .map(([width, data]) => {
            return normalizeUrlSlashes(`${srcBase}/${width}x/dam/${imgId}${imgPath}) ${width}w`);
        })
        .join(', ');

    // first rendition fallback to original
    const src = normalizeUrlSlashes(`${srcBase}/400x/dam/${imgId}${imgPath}`);
    const alt = `${metadata?.description ?? image?.['@name']}`;

    return {
        src,
        srcset: srcset || src,
        alt,
    };
}
